import { apiClient } from './apiClient';
import { indexedDbService } from './indexedDbService';
import { offlineService } from './offlineService';

export interface Form {
  id: number;
  businessId: string;
  code: string;
  description: string;
  displayOrder: number;
  numberOfColumns: number;
  pdfConfigurationJson?: string;
  headerConfigurationJson?: string;
  createdDate: string;
  modifiedDate?: string;
  isActive: boolean;
  isPrintable: boolean;
  formSections: FormSection[];
}

export interface FormSection {
  id: number;
  formId: number;
  form?: Form;
  code: string;
  description: string;
  columnNumber: number;
  displayOrder: number;
  createdDate: string;
  modifiedDate?: string;
  isActive: boolean;
  formFields: FormField[];
}

export interface FormField {
  id: number;
  formSectionId: number;
  formSection?: FormSection;
  code: string;
  description: string;
  displayOrder: number;
  columnNumber: number; // 0 = full width, 1 = left, 2 = right
  fieldType: FormFieldType | string; // API returns string enum names, TypeScript uses numeric enum
  formFieldDataTypeId?: number;
  info?: string;
  configurationJson?: string;
  isRequired: boolean;
  createdDate: string;
  modifiedDate?: string;
  isActive: boolean;
  formFieldDataType?: FormFieldDataType;
}

export interface FormFieldDataType {
  id: number;
  businessId: string;
  code: string;
  description: string;
  displayOrder: number;
  category: DataTypeCategory;
  createdDate: string;
  modifiedDate?: string;
  isActive: boolean;
  formFieldDataItems: FormFieldDataItem[];
}

export interface FormFieldDataItem {
  id: number;
  formFieldDataTypeId: number;
  code: string;
  description: string;
  displayOrder: number;
  createdDate: string;
  modifiedDate?: string;
  isActive: boolean;
}

export enum FormFieldType {
  String = 1,
  Multiline = 2,
  Bool = 3,
  Dimension = 4,
  Int = 5,
  Dropdown = 6,
  Date = 7,
  DateTime = 8,
  CheckboxList = 9,
  HorizontalRule = 10,
  TextboxWithCheckbox = 11
}

export enum DataTypeCategory {
  Dropdown = 1,
  CheckboxList = 2
}

export interface ParameterChangeDto {
  fieldId: number;
  fieldCode: string;
  fieldDescription: string;
  currentValue: string;
  newValue: string;
  sourceDescription: string;
}

export interface ParameterChangesResponse {
  changes: ParameterChangeDto[];
  hasChanges: boolean;
}

export interface FormResponse {
  id: number;
  formId: number;
  jobId?: string;
  customerId?: string;
  responseData: string; // JSON string
  submittedBy: string;
  submittedDate: string;
  isActive: boolean;
}

export interface CreateFormResponseDto {
  formId: number;
  jobId?: string;
  customerId?: string;
  responseData: string; // JSON string
}

export interface JobFormResponseDto {
  id: string;
  jobId: string;
  formId: number;
  value: string; // JSON string containing all form field values
  isActive: boolean;
  createdDate: string;
  modifiedDate?: string;
  createdById: string;
  modifiedById?: string;
}

class FormService {
  async getForms(): Promise<Form[]> {
    try {
      const isOnline = offlineService.getOnlineStatus();

      if (isOnline) {
        console.log('[FormService] Loading forms from API');
        const response = await apiClient.get('/forms');
        return response.data;
      } else {
        console.log('[FormService] Loading forms from IndexedDB (offline mode)');
        return await indexedDbService.getForms();
      }
    } catch (error: any) {
      console.error('[FormService] API failed, trying IndexedDB fallback:', error);

      // Fallback to IndexedDB
      try {
        return await indexedDbService.getForms();
      } catch (indexedDbError: any) {
        console.error('[FormService] IndexedDB fallback failed:', indexedDbError);
        throw new Error('Failed to load forms from both API and offline storage');
      }
    }
  }

  async getForm(id: number): Promise<Form> {
    try {
      const isOnline = offlineService.getOnlineStatus();

      if (isOnline) {
        console.log('[FormService] Loading form from API');
        const response = await apiClient.get(`/forms/${id}`);
        return response.data;
      } else {
        console.log('[FormService] Loading form from IndexedDB (offline mode)');
        return await indexedDbService.getForm(id);
      }
    } catch (error: any) {
      console.error('[FormService] API failed, trying IndexedDB fallback:', error);

      // Fallback to IndexedDB
      try {
        return await indexedDbService.getForm(id);
      } catch (indexedDbError: any) {
        console.error('[FormService] IndexedDB fallback failed:', indexedDbError);
        throw new Error('Failed to load form from both API and offline storage');
      }
    }
  }

  async updateForm(id: number, form: Partial<Form>): Promise<Form> {
    try {
      console.log('[FormService] Updating form via API');
      const response = await apiClient.put(`/forms/${id}`, form);
      return response.data;
    } catch (error: any) {
      console.error('[FormService] Failed to update form:', error);
      throw error;
    }
  }

  async getFormResponses(formId: number, jobId?: string): Promise<FormResponse[]> {
    try {
      const isOnline = offlineService.getOnlineStatus();

      if (isOnline) {
        console.log('[FormService] Loading form responses from API');
        const params = new URLSearchParams();
        if (jobId) params.append('jobId', jobId);

        const response = await apiClient.get(`/forms/${formId}/responses?${params.toString()}`);
        return response.data;
      } else {
        console.log('[FormService] Loading form responses from IndexedDB (offline mode)');
        return await indexedDbService.getFormResponses(formId, jobId);
      }
    } catch (error: any) {
      console.error('[FormService] API failed, trying IndexedDB fallback:', error);

      // Fallback to IndexedDB
      try {
        return await indexedDbService.getFormResponses(formId, jobId);
      } catch (indexedDbError: any) {
        console.error('[FormService] IndexedDB fallback failed:', indexedDbError);
        throw new Error('Failed to load form responses from both API and offline storage');
      }
    }
  }

  async getJobFormResponses(jobId: string): Promise<JobFormResponseDto[]> {
    try {
      const isOnline = offlineService.getOnlineStatus();

      if (isOnline) {
        console.log('[FormService] Loading job form responses from API');
        const response = await apiClient.get(`/forms/jobs/${jobId}/responses`);
        return response.data;
      } else {
        console.log('[FormService] Loading job form responses from IndexedDB (offline mode)');
        // For offline, we'd need to implement this in IndexedDB service
        // For now, return empty array
        return [];
      }
    } catch (error: any) {
      console.error('[FormService] API failed, trying IndexedDB fallback:', error);

      // Fallback to IndexedDB
      try {
        // For offline, we'd need to implement this in IndexedDB service
        // For now, return empty array
        return [];
      } catch (indexedDbError: any) {
        console.error('[FormService] IndexedDB fallback failed:', indexedDbError);
        throw new Error('Failed to load job form responses from both API and offline storage');
      }
    }
  }

  // Transform backend format (array of field responses) to frontend format (object keyed by field code)
  transformJobFormResponsesToFormData(
    responses: JobFormResponseDto[],
    form: Form
  ): { [key: string]: any } {
    // NEW FORMAT: responses array contains one item per form with all values as JSON
    // Find the response for this specific form
    const formResponse = responses.find(r => r.formId === form.id);

    if (!formResponse || !formResponse.value) {
      return {}; // No response data for this form
    }

    try {
      // Parse the JSON value which contains all field values
      const formData = JSON.parse(formResponse.value);

      // Expand checkboxlist arrays into individual checkbox fields for PDF viewer compatibility
      const expandedFormData = this.expandCheckboxListsForPdf(formData, form);

      return expandedFormData || {};
    } catch (error) {
      console.error('Failed to parse form response JSON:', error);
      return {};
    }
  }

  /**
   * Expands checkboxlist array values into individual checkbox fields for PDF viewer compatibility.
   * For example: DOOR_TYPE: ['MELTECA', 'PANELFORM'] becomes:
   * DOOR_TYPE: ['MELTECA', 'PANELFORM'], DOOR_TYPE_MELTECA: true, DOOR_TYPE_PANELFORM: true
   */
  private expandCheckboxListsForPdf(formData: { [key: string]: any }, form: Form): { [key: string]: any } {
    const expandedData = { ...formData };

    // Find all CheckboxList fields in the form
    if (form?.formSections) {
      form.formSections.forEach((section) => {
        section.formFields?.forEach((field) => {
          const fieldTypeMap: { [key: number]: string } = {
            1: 'String', 2: 'Multiline', 3: 'Bool', 4: 'Dimension', 5: 'Int',
            6: 'Dropdown', 7: 'Date', 8: 'DateTime', 9: 'CheckboxList',
            10: 'HorizontalRule', 11: 'TextboxWithCheckbox'
          };
          const fieldType = typeof field.fieldType === 'string'
            ? field.fieldType
            : (fieldTypeMap[field.fieldType as number] || 'String');

          if (fieldType === 'CheckboxList') {
            const fieldValue = formData[field.code];

            // If the field value is an array, expand it into individual checkbox fields
            if (Array.isArray(fieldValue)) {
              // Get all available items for this checkboxlist
              const items = field.formFieldDataType?.formFieldDataItems || [];

              // Create individual checkbox fields for each item
              items.forEach((item) => {
                const checkboxFieldName = `${field.code}_${item.code}`;
                // Set to true if the item code is in the array, false otherwise
                expandedData[checkboxFieldName] = fieldValue.includes(item.code);
              });
            }
          }
        });
      });
    }

    return expandedData;
  }

  async submitFormResponse(
    form: Form,
    jobId: string,
    formData: { [key: string]: any }
  ): Promise<any> {
    try {
      const isOnline = offlineService.getOnlineStatus();

      // Clean up form data before sending:
      // 1. Remove individual checkbox item fields (e.g., DOOR_TYPE_MELTECA)
      // 2. Convert stringified arrays to real arrays
      // 3. Only keep valid form field codes
      const cleanedFormData = this.cleanFormDataForSubmit(form, formData);

      if (isOnline) {
        console.log('[FormService] Submitting form response via API');
        console.log('[FormService] Cleaned form data:', cleanedFormData);

        // NEW FORMAT: Send formData directly as JSON object
        // The backend now stores all field values in a single JSON column per form
        const response = await apiClient.post(
          `/forms/jobs/${jobId}/forms/${form.id}/response`,
          cleanedFormData
        );
        return response.data;
      } else {
        console.log('[FormService] Storing form response in IndexedDB (offline mode)');
        // For offline, store in the old format
        const responseData = {
          formId: form.id,
          jobId,
          customerId: '',
          responseData: JSON.stringify(cleanedFormData)
        };
        return await indexedDbService.createFormResponse(responseData);
      }
    } catch (error: any) {
      console.error('[FormService] API failed, trying IndexedDB fallback:', error);

      // Fallback to IndexedDB
      try {
        const cleanedFormData = this.cleanFormDataForSubmit(form, formData);
        const responseData = {
          formId: form.id,
          jobId,
          customerId: '',
          responseData: JSON.stringify(cleanedFormData)
        };
        return await indexedDbService.createFormResponse(responseData);
      } catch (indexedDbError: any) {
        console.error('[FormService] IndexedDB fallback failed:', indexedDbError);
        throw new Error('Failed to submit form response to both API and offline storage');
      }
    }
  }

  /**
   * Cleans form data before submitting:
   * - Removes individual checkbox item fields (e.g., DOOR_TYPE_MELTECA)
   * - Converts stringified arrays to real arrays
   * - Only keeps valid form field codes
   */
  private cleanFormDataForSubmit(form: Form, formData: { [key: string]: any }): { [key: string]: any } {
    const cleanedData: { [key: string]: any } = {};

    // Build a set of valid field codes from the form definition
    const validFieldCodes = new Set<string>();
    const checkboxListFieldCodes = new Set<string>(); // Parent field codes like "DOOR_TYPE"
    const checkboxListItemPatterns = new Map<string, string>(); // Maps pattern "DOOR_TYPE_" -> parent code "DOOR_TYPE"
    const dimensionFieldCodes = new Set<string>(); // Parent field codes like "MICROWAVE_DIMENSIONS"
    const dimensionItemPatterns = new Map<string, string>(); // Maps pattern "MICROWAVE_DIMENSIONS_" -> parent code
    const textboxWithCheckboxFieldCodes = new Set<string>(); // Parent field codes like "BIN_SYSTEMS"
    const textboxWithCheckboxItemPatterns = new Map<string, string>(); // Maps pattern "BIN_SYSTEMS_" -> parent code

    if (form?.formSections) {
      form.formSections.forEach((section) => {
        section.formFields?.forEach((field) => {
          validFieldCodes.add(field.code);

          // For CheckboxList, Dimension, and TextboxWithCheckbox fields, track the pattern for individual item fields
          const fieldTypeMap: { [key: number]: string } = {
            1: 'String', 2: 'Multiline', 3: 'Bool', 4: 'Dimension', 5: 'Int',
            6: 'Dropdown', 7: 'Date', 8: 'DateTime', 9: 'CheckboxList',
            10: 'HorizontalRule', 11: 'TextboxWithCheckbox'
          };
          const fieldType = typeof field.fieldType === 'string'
            ? field.fieldType
            : (fieldTypeMap[field.fieldType as number] || 'String');

          if (fieldType === 'CheckboxList') {
            checkboxListFieldCodes.add(field.code);
            checkboxListItemPatterns.set(`${field.code}_`, field.code);
          } else if (fieldType === 'Dimension') {
            dimensionFieldCodes.add(field.code);
            dimensionItemPatterns.set(`${field.code}_`, field.code);
          } else if (fieldType === 'TextboxWithCheckbox') {
            textboxWithCheckboxFieldCodes.add(field.code);
            textboxWithCheckboxItemPatterns.set(`${field.code}_`, field.code);
          }
        });
      });
    }

    // Aggregate checked individual checkbox items into arrays
    const checkboxListValues = new Map<string, string[]>();
    // Aggregate dimension components (H, W, D) into formatted strings
    const dimensionValues = new Map<string, { H?: string, W?: string, D?: string }>();
    // Aggregate textbox with checkbox components (text, checkbox) into objects
    const textboxWithCheckboxValues = new Map<string, { text?: string, checked?: boolean }>();

    // Track which CheckboxList parents have at least one individual item entry in formData.
    // Rebuild the parent from items when present, preserve it otherwise. Relied on by:
    //   - User edits: renderer toggles per-item state; the stale parent must be rebuilt so
    //     unchecking an item actually persists.
    //   - Load-then-save with no edits: server returns only the parent (e.g. ["YES"]) and
    //     the renderer hasn't written per-item state yet; preserve the parent verbatim.
    //   - Offline re-submit: queued formData may contain only the parent for the same reason.
    const checkboxListParentsWithItems = new Set<string>();
    for (const key of Object.keys(formData)) {
      for (const [pattern, parentCode] of checkboxListItemPatterns.entries()) {
        if (key.startsWith(pattern)) {
          checkboxListParentsWithItems.add(parentCode);
          break;
        }
      }
    }

    // Process each field in formData
    for (const [key, value] of Object.entries(formData)) {
      // Check if this is an individual checkbox item field (e.g., DOOR_TYPE_MELTECA)
      let parentFieldCode: string | null = null;
      for (const [pattern, parentCode] of checkboxListItemPatterns.entries()) {
        if (key.startsWith(pattern) && typeof value === 'boolean') {
          parentFieldCode = parentCode;
          break;
        }
      }

      if (parentFieldCode) {
        // This is an individual checkbox item - aggregate it
        if (value === true) {
          // Extract the item code (e.g., "MELTECA" from "DOOR_TYPE_MELTECA")
          const itemCode = key.substring(parentFieldCode.length + 1);
          if (!checkboxListValues.has(parentFieldCode)) {
            checkboxListValues.set(parentFieldCode, []);
          }
          checkboxListValues.get(parentFieldCode)!.push(itemCode);
        }
        continue; // Skip this field (don't add to cleanedData)
      }

      // If this key is a CheckboxList parent and we've seen item entries for it,
      // skip — the parent will be rebuilt from items below. Otherwise preserve it.
      if (checkboxListFieldCodes.has(key) && checkboxListParentsWithItems.has(key)) {
        continue;
      }

      // Check if this is an individual dimension component field (e.g., MICROWAVE_DIMENSIONS_H)
      let dimensionParentCode: string | null = null;
      let dimensionComponent: 'H' | 'W' | 'D' | null = null;
      for (const [pattern, parentCode] of dimensionItemPatterns.entries()) {
        if (key.startsWith(pattern)) {
          const suffix = key.substring(pattern.length);
          if (suffix === 'H' || suffix === 'W' || suffix === 'D') {
            dimensionParentCode = parentCode;
            dimensionComponent = suffix;
            break;
          }
        }
      }

      if (dimensionParentCode && dimensionComponent) {
        // This is an individual dimension component - aggregate it
        if (!dimensionValues.has(dimensionParentCode)) {
          dimensionValues.set(dimensionParentCode, {});
        }
        dimensionValues.get(dimensionParentCode)![dimensionComponent] = value?.toString() || '';
        continue; // Skip this field (don't add to cleanedData)
      }

      // Check if this is an individual textbox with checkbox component field (e.g., BIN_SYSTEMS_text, BIN_SYSTEMS_checkbox)
      let textboxWithCheckboxParentCode: string | null = null;
      let textboxWithCheckboxComponent: 'text' | 'checkbox' | null = null;
      for (const [pattern, parentCode] of textboxWithCheckboxItemPatterns.entries()) {
        if (key.startsWith(pattern)) {
          const suffix = key.substring(pattern.length);
          if (suffix === 'text' || suffix === 'checkbox') {
            textboxWithCheckboxParentCode = parentCode;
            textboxWithCheckboxComponent = suffix;
            break;
          }
        }
      }

      if (textboxWithCheckboxParentCode && textboxWithCheckboxComponent) {
        // This is an individual textbox with checkbox component - aggregate it
        if (!textboxWithCheckboxValues.has(textboxWithCheckboxParentCode)) {
          textboxWithCheckboxValues.set(textboxWithCheckboxParentCode, {});
        }
        if (textboxWithCheckboxComponent === 'text') {
          textboxWithCheckboxValues.get(textboxWithCheckboxParentCode)!.text = value?.toString() || '';
        } else {
          textboxWithCheckboxValues.get(textboxWithCheckboxParentCode)!.checked = Boolean(value);
        }
        continue; // Skip this field (don't add to cleanedData)
      }

      // Convert stringified arrays to real arrays
      if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            cleanedData[key] = parsed;
            continue;
          }
        } catch {
          // Not valid JSON, keep as string
        }
      }

      // Keep all other values as-is
      cleanedData[key] = value;
    }

    // Add aggregated checkbox list values
    for (const [fieldCode, items] of checkboxListValues.entries()) {
      cleanedData[fieldCode] = items;
    }

    // Ensure empty arrays for checkbox list fields that had no checked items
    for (const fieldCode of checkboxListFieldCodes) {
      if (!cleanedData[fieldCode]) {
        cleanedData[fieldCode] = [];
      }
    }

    // Add aggregated dimension values
    for (const [fieldCode, components] of dimensionValues.entries()) {
      const h = components.H || '';
      const w = components.W || '';
      const d = components.D || '';
      cleanedData[fieldCode] = `H:${h} W:${w} D:${d}`;
    }

    // Ensure empty dimension strings for dimension fields that had no components
    for (const fieldCode of dimensionFieldCodes) {
      if (!cleanedData[fieldCode]) {
        cleanedData[fieldCode] = 'H: W: D:';
      }
    }

    // Add aggregated textbox with checkbox values
    for (const [fieldCode, components] of textboxWithCheckboxValues.entries()) {
      const text = components.text || '';
      const checked = components.checked || false;
      cleanedData[fieldCode] = { text, checked };
    }

    // Ensure empty objects for textbox with checkbox fields that had no components
    for (const fieldCode of textboxWithCheckboxFieldCodes) {
      if (!cleanedData[fieldCode]) {
        cleanedData[fieldCode] = { text: '', checked: false };
      }
    }

    return cleanedData;
  }

  async getFormFieldParameterValues(jobId: string, formId: number): Promise<{ [fieldId: number]: string }> {
    try {
      const isOnline = offlineService.getOnlineStatus();

      if (isOnline) {
        console.log('[FormService] Loading form field parameter values from API');
        const response = await apiClient.get(`/forms/jobs/${jobId}/forms/${formId}/parameter-values`);
        return response.data || {};
      } else {
        console.log('[FormService] Offline mode - cannot load form parameter values');
        // For offline mode, we can't resolve form parameter values from other forms
        // Return empty object
        return {};
      }
    } catch (error: any) {
      console.error('[FormService] Failed to load form parameter values:', error);
      // Return empty object on error to allow form to continue loading
      return {};
    }
  }

  async getParameterChanges(jobId: string, formId: number): Promise<ParameterChangesResponse> {
    try {
      const isOnline = offlineService.getOnlineStatus();

      if (!isOnline) {
        console.log('[FormService] Offline mode - cannot check parameter changes');
        return { changes: [], hasChanges: false };
      }

      console.log('[FormService] Checking for parameter changes');
      const response = await apiClient.get(`/forms/jobs/${jobId}/forms/${formId}/parameter-changes`);
      return response.data || { changes: [], hasChanges: false };
    } catch (error: any) {
      console.error('[FormService] Failed to check parameter changes:', error);
      return { changes: [], hasChanges: false };
    }
  }

  getFieldTypeDisplayName(fieldType: FormFieldType): string {
    const typeNames: { [key in FormFieldType]: string } = {
      [FormFieldType.String]: 'Text',
      [FormFieldType.Multiline]: 'Multiline Text',
      [FormFieldType.Bool]: 'Checkbox',
      [FormFieldType.Dimension]: 'Dimension',
      [FormFieldType.Int]: 'Number',
      [FormFieldType.Dropdown]: 'Dropdown',
      [FormFieldType.Date]: 'Date',
      [FormFieldType.DateTime]: 'Date & Time',
      [FormFieldType.CheckboxList]: 'Checkbox List',
      [FormFieldType.HorizontalRule]: 'Horizontal Rule',
      [FormFieldType.TextboxWithCheckbox]: 'Textbox with Checkbox'
    };
    return typeNames[fieldType] || 'Unknown';
  }

  parseConfigurationJson(configJson?: string): any {
    if (!configJson) return {};
    try {
      return JSON.parse(configJson);
    } catch (error) {
      console.error('Failed to parse configuration JSON:', error);
      return {};
    }
  }

  /**
   * Get a context-specific configuration value (web, pdf, or both/default)
   * Supports both legacy string format and new object format:
   * - Legacy: "left" (applies to both web and pdf)
   * - New: { web: "left", pdf: "right" } or { web: "left", pdf: "right", default: "center" }
   */
  getContextValue(config: any, property: string, context: 'web' | 'pdf'): any {
    const value = config?.[property];
    if (value === undefined || value === null) {
      return undefined;
    }

    // If it's an object with context-specific values
    if (typeof value === 'object' && !Array.isArray(value)) {
      // Try context-specific value first
      if (value[context] !== undefined) {
        return value[context];
      }
      // Fall back to default value
      if (value.default !== undefined) {
        return value.default;
      }
      // If no default and no context match, return undefined
      return undefined;
    }

    // Legacy format - applies to both contexts
    return value;
  }
}

export const formService = new FormService();
